import { useEffect, useState } from "react";
import { type Dayjs } from "dayjs";
import { useTenant } from "hooks/settings";
import { backendApi } from "src/store/api/enhancedApi";
import { useAppDispatch } from "src/store/store";
import type { SplitPeriod } from "../../hooks/filterBucketsBySplit";
import type {
  ModelIdentity,
  ModelBucketGroup,
  ModelComparisonResult,
} from "../types";

export const useModelComparisonData = (
  models: readonly ModelIdentity[],
  start: Dayjs | null,
  end: Dayjs | null
): ModelComparisonResult => {
  const tenant = useTenant();
  const dispatch = useAppDispatch();

  const [bucketGroups, setBucketGroups] = useState<readonly ModelBucketGroup[]>(
    []
  );
  const [failedModels, setFailedModels] = useState<readonly ModelIdentity[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [splitPeriods, setSplitPeriods] = useState<readonly SplitPeriod[]>([]);

  useEffect(() => {
    if (!start || !end || models.length === 0) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets state when inputs are empty
      setBucketGroups([]);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets state when inputs are empty
      setFailedModels([]);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets state when inputs are empty
      setIsLoading(false);
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets state when inputs are empty
      setSplitPeriods([]);
      return;
    }

    let cancelled = false;
    const bucketSizeSeconds = Math.floor(end.diff(start, "second"));

    // eslint-disable-next-line @eslint-react/set-state-in-effect -- signals loading before async fetch begins
    setIsLoading(true);

    const subscription = dispatch(
      backendApi.endpoints.getPerformanceTimeseries.initiate({
        tenantName: tenant,
        start: start.toISOString(),
        end: end.toISOString(),
        targetLocation: models.map((m) => m.location),
        labellingName: models.map((m) => m.labellingName),
        bucketSizeSeconds,
      })
    );

    void subscription.unwrap().then(
      (response) => {
        if (cancelled) return;

        const resolvedGroups: ModelBucketGroup[] = [];
        for (const modelResult of response.results) {
          if (modelResult.buckets.length > 0) {
            resolvedGroups.push({
              model: {
                labellingName: modelResult.labelling_name,
                location: modelResult.target_location,
              },
              buckets: modelResult.buckets,
            });
          }
        }

        setBucketGroups(resolvedGroups);
        setFailedModels([]);
        setSplitPeriods(response.split_periods);
        setIsLoading(false);
      },
      () => {
        if (cancelled) return;
        setBucketGroups([]);
        setFailedModels([...models]);
        setSplitPeriods([]);
        setIsLoading(false);
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [models, start, end, tenant, dispatch]);

  return { bucketGroups, failedModels, isLoading, splitPeriods };
};
